import { Link, useParams } from 'react-router-dom';
import { Puzzle } from 'lucide-react';
import { Separator } from '@reflex/ui/components/ui/separator';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { PageLayout } from '@/components/layout/PageLayout';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { usePlugins } from '@/hooks/config/usePlugins';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { BackLink } from '@/components/shared/BackLink';
import {
  PluginHeroSection,
  PluginSetupStepsSection,
  PluginSecretsSection,
  PluginAdminSetupPanel,
} from './plugin-detail';
import { ROUTES } from '@/lib/routes';

/**
 * Per-plugin admin setup route mounted at `/admin/plugins/:name/setup`. Hosts
 * the service-wide configuration UI for a single plugin:
 *
 * 1. The plugin's hero (icon, name, version, description) so the page reads
 *    as "GitHub setup" / "Slack setup" rather than a generic admin form.
 * 2. The generic `PluginSetupStepsSection` and `PluginSecretsSection` derived
 *    from the plugin's `setupSteps` / `requiredSecrets` declarations.
 * 3. A `PluginAdminSetupPanel` slot that renders the plugin-contributed
 *    `adminSetupComponent` (e.g. GitHub's App identity card + Setup URL
 *    widget). Plugins without a contributed component still get the generic
 *    steps surface.
 *
 * The same `PluginAdminSetupPanel` is embedded inline by the first-run
 * `/setup` wizard so plugins implement the panel once.
 */
export function AdminPluginSetupPage() {
  const { pluginName } = useParams<{ pluginName: string }>();
  const { manifests, isLoading } = usePlugins();
  const plugin = manifests.find((p) => p.name === pluginName);
  const backLink = (
    <BackLink to={plugin ? ROUTES.admin.plugin(plugin.name) : ROUTES.admin.plugins}>
      {plugin?.name ?? 'Plugins'}
    </BackLink>
  );

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader left={backLink} />
        <LoadingState message="Loading plugin..." />
      </div>
    );
  }

  if (!plugin) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader left={backLink} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
          <Puzzle className="h-8 w-8 text-muted-foreground/40" />
          <PageDescription className="mt-0 text-center text-sm">Plugin not found</PageDescription>
          <Link to={ROUTES.admin.plugins} className="text-xs underline hover:text-foreground">
            Back to plugins
          </Link>
        </div>
      </div>
    );
  }

  const secrets = plugin.server?.requiredSecrets ?? [];
  const setupSteps = plugin.server?.setupSteps ?? [];
  const hasAnything =
    secrets.length > 0 || setupSteps.length > 0 || !!plugin.web?.adminSetupComponent;

  return (
    <div className="flex h-full flex-col" data-testid="admin-plugin-setup-page">
      <PanelHeader left={backLink} />

      <PageLayout maxWidth="3xl">
        <PluginHeroSection plugin={plugin} />

        {!hasAnything && (
          <PageDescription className="mt-0 text-sm">
            This plugin has no service-level setup. Per-organization configuration lives on the
            plugin's own integration page.
          </PageDescription>
        )}

        {setupSteps.length > 0 && (
          <>
            <Separator />
            <PluginSetupStepsSection steps={setupSteps} />
          </>
        )}

        {secrets.length > 0 && (
          <>
            <Separator />
            <PluginSecretsSection secrets={secrets} pluginName={plugin.name} />
          </>
        )}

        {plugin.web?.adminSetupComponent && (
          <>
            <Separator />
            <PluginAdminSetupPanel plugin={plugin} />
          </>
        )}
      </PageLayout>
    </div>
  );
}

export default AdminPluginSetupPage;
